// Create a Timer class with common functionality
class Timer extends HTMLElement {
    constructor() {
        super();
        this.endDate = null;
        this.currentTime = null;
        this.timeRemaining = 0;
        this.timer = '';
        this.showSeconds = false;
        this.digitTitles = {
            days: '',
            hours: '',
            minutes: '',
            seconds: ''
        };
    }

    initializeData(jsonData) {
        this.endDate = new Date(jsonData.endDate);
        this.currentTime = new Date();
        this.timeRemaining = this.endDate - this.currentTime;
        this.digitTitles = jsonData.digitTitles;
        this.showSeconds = jsonData.showSeconds;

        // Move createVariables call here to initialize parsedBeforeText and parsedEndText
        this.createVariables();
    }

    createVariables() {
        if (Date.parse(this.endDate)) {
            const { days, hours, minutes, seconds } = this.calculateTimeUnits(this.timeRemaining);

            this.endDayFormatted = this.formatDate(this.endDate, 'dddd');

            this.endDateFormatted = '';
            this.endDateDayFormatted = '';
            this.endDateMonthFormatted = '';
            this.endDateYearFormatted = '';

            if (this.endDate) {
                this.endDateFormatted = this.formatDate(this.endDate, 'Do MMMM YYYY');
                this.endDateDayFormatted = this.formatDate(this.endDate, 'Do');
                this.endDateMonthFormatted = this.formatDate(this.endDate, 'MMMM');
                this.endDateYearFormatted = this.formatDate(this.endDate, 'YYYY');
            }

            // Set this.timer to the timer element's HTML content
            this.timer = this.createClockElement().outerHTML;
        }
    }

    createClockElement() {
        const { days, hours, minutes, seconds } = this.calculateTimeUnits(this.timeRemaining);

        let clock = document.createElement('clock');
        let daysElement = document.createElement('days');
        let hoursElement = document.createElement('hours');
        let minutesElement = document.createElement('minutes');
        let secondsElement = document.createElement('seconds');

        daysElement.setAttribute('data-title', this.digitTitles.days);
        hoursElement.setAttribute('data-title', this.digitTitles.hours);
        minutesElement.setAttribute('data-title', this.digitTitles.minutes);
        secondsElement.setAttribute('data-title', this.digitTitles.seconds);

        clock.appendChild(daysElement);
        clock.appendChild(hoursElement);
        clock.appendChild(minutesElement);
        clock.appendChild(secondsElement);

        return clock;
    }

    calculateTimeUnits(timeRemaining) {
        const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
        return { days, hours, minutes, seconds };
    }

    formatDate(date, format) {
        // Check if the date is a valid date
        if (Date.parse(date)) {
            // console.warn('Invalid date:', date);
            return 'Invalid date';
        }

        // Create a new date object from the date string
        const newDate = new Date(date);

        // Create an array of the days of the week
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

        // Create an array of the months of the year
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
            'October', 'November', 'December'];

        // Get the day, month, and year from the date object
        const day = newDate.getDate();
        const month = newDate.getMonth();
        const year = newDate.getFullYear();

        // Return the formatted date
        return format
            .replace('dddd', days[newDate.getDay()]) // e.g. Monday
            .replace('ddd', days[newDate.getDay()].substring(0, 3)) // e.g. Mon
            .replace('dd', day.toString().padStart(2, '0')) // e.g. 01
            .replace('Do', day.toString() + this.getOrdinal(day)) // e.g. 1st
            .replace('MMMM', months[month]) // e.g. January
            .replace('MMM', months[month].substring(0, 3)) // e.g. Jan
            .replace('MM', (month + 1).toString().padStart(2, '0')) // e.g. 01
            .replace('YYYY', year.toString()) // e.g. 2020
            .replace('YY', year.toString().substring(2, 4)); // e.g. 20
    }

    getOrdinal(number) {
        // Get the last digit of the number
        const lastDigit = number % 10;

        // If the last digit is 1, 2, or 3, return the correct ordinal
        if (lastDigit === 1 && number !== 11) {
            return 'st';
        } else if (lastDigit === 2 && number !== 12) {
            return 'nd';
        } else if (lastDigit === 3 && number !== 13) {
            return 'rd';
        } else {
            return 'th';
        }
    }
}
class CountdownTimer extends Timer {
    constructor() {
        super();
        this.beforeText = '';
        this.endedText = '';
        this.styles = {
            color: '',
            backgroundColor: '',
            justifyContent: '',
        }
    }

    connectedCallback() {
        const scriptElement = this.querySelector('script[type="application/json"]');

        if (!scriptElement) {
            return;
        }

        try {
            const jsonData = JSON.parse(scriptElement.textContent.trim());

            this.initializeData(jsonData);
            this.addStyles();
            this.renderTimer();
            this.startTimer();
        } catch (error) {
            console.error('Error parsing JSON:', error);
        }
    }

    addStyles() {
        this.style.backgroundColor = this.styles.backgroundColor;
        this.style.color = this.styles.color;
        this.style.justifyContent = this.styles.justifyContent;
    }

    initializeData(jsonData) {
        super.initializeData(jsonData);
        this.beforeText = this.decodeText(jsonData.beforeText);
        this.endedText = this.decodeText(jsonData.endedText);
        this.styles = jsonData.styles;

        this.createVariables();
        this.parseVariables();

        // Check if the timer has already ended
        if (this.timeRemaining <= 0) {
            this.innerHTML = `<p>${this.parsedEndText}</p>`;
            this.showTimer();
        }
    }

    parseVariables() {
        this.parsedBeforeText = this.replaceVariables(this.beforeText);
        this.parsedEndText = this.replaceVariables(this.endedText);
    }

    decodeText(text) {
        if (text === '') {
            return text;
        }
        text = decodeURIComponent(encodeURIComponent(text));
        text = text.replace('&lcub;', '{');
        text = text.replace('&rcub;', '}');

        return text;
    }

    replaceVariables(text) {
        if (typeof text !== 'string') {
            return text;
        }

        text = this.decodeText(text);
        this.createVariables();

        if (Date.parse(this.endDate)) {
            text = text
            .replace('{TIMER}', this.timer)
            .replace('{END-DAY}', this.endDayFormatted)
            .replace('{END-DATE}', this.endDateFormatted)
            .replace('{END-DATE-DAY}', this.endDateDayFormatted)
            .replace('{END-DATE-MONTH}', this.endDateMonthFormatted)
            .replace('{END-DATE-YEAR}', this.endDateYearFormatted);
        }

        return text;
    }

    renderTimer() {
        let text = this.parsedBeforeText;
        if (this.timeRemaining <= 0) {
            text = this.parsedEndText;
        }

        this.innerHTML = text;
        this.showTimer();
    }

    showTimer() {
        if ((this.timeRemaining > 0 && this.beforeText !== '' && this.beforeText != undefined && this.beforeText != 'null')
            || (this.timeRemaining <= 0 && this.endedText !== '' && this.endedText != undefined && this.endedText != 'null')) {
            this.classList.add('visible');
        } else if (!Date.parse(this.endDate) && this.beforeText !== '' && this.beforeText != undefined && this.beforeText != 'null') {
            this.classList.add('visible');
        }

        if(isNaN(this.timeRemaining)){
          //
          if(this.innerText == 'null'){
            console.log("invalid time, hiding...", this.innerText);
            this.style.display = 'none';
          }
        }
    }

    startTimer() {

        if (!Date.parse(this.endDate)) {
            return;
        }

        const updateTimer = () => {
            this.currentTime = new Date();
            this.timeRemaining = this.endDate - this.currentTime;

            if (this.timeRemaining <= 0) {
                clearInterval(this.intervalId);
                this.innerHTML = `<p>${this.endedText}</p>`;

                if (this.endedText === '' || this.endedText == undefined || this.endedText == 'null') {
                    this.remove();
                }
            } else {
                const { days, hours, minutes, seconds } = this.calculateTimeUnits(this.timeRemaining);

                let daysElement = this.querySelector('days');
                let hoursElement = this.querySelector('hours');
                let minutesElement = this.querySelector('minutes');
                let secondsElement = this.querySelector('seconds');

                if (!daysElement) {
                    return;
                }

                // Update attributes on individual elements
                daysElement.setAttribute('data-digit', days);
                hoursElement.setAttribute('data-digit', hours);
                minutesElement.setAttribute('data-digit', minutes);
                secondsElement.setAttribute('data-digit', seconds);

                if (days === 0 && hours === 0 && minutes === 0) {
                    secondsElement.style.display = 'block';
                } else if (!this.showSeconds) {
                    secondsElement.style.display = 'none';
                }

                if (days === 0 && hours === 0 && minutes === 0) {
                    this.querySelector('days').style.display = 'none';
                    this.querySelector('hours').style.display = 'none';
                    this.querySelector('minutes').style.display = 'none';

                } else if (days === 0 && hours === 0) {
                    this.querySelector('days').style.display = 'none';
                    this.querySelector('hours').style.display = 'none';
                } else if (days === 0) {
                    this.querySelector('days').style.display = 'none';
                }
            }
        };

        // Update the timer initially
        updateTimer();

        // Use setInterval for non-animation updates
        this.intervalId = setInterval(updateTimer, 1000);
    }
}

window.customElements.define('countdown-timer', CountdownTimer);
